import { ComponentType, ReactNode, useState } from 'react';

import classNames from 'classnames';

import BaseInfoTooltip from '@shared/common/components/BaseInfoTooltip';

import TableOfContentLayout from '@launchpad/components/layout/TableOfContentLayout';
import {
  PROJECT_CONTENT_TAB_TYPE,
  ProjectContentData,
  ProjectContentTab,
  ProjectContentTabType,
} from '@launchpad/data/projects/types';

interface ProjectContentTabProps {
  tab: ProjectContentTab;
  children?: ReactNode;
}

const ProjectContentTableOfContentComponent = ({ tab }: ProjectContentTabProps) => {
  if (tab.type !== PROJECT_CONTENT_TAB_TYPE.TABLE_OF_CONTENT) {
    return null;
  }

  return (
    <TableOfContentLayout>
      <div className="flex flex-col gap-6 sm:gap-10">
        {tab.content.map(({ id, title, content }) => {
          return (
            <section key={id} className="[&>p+p:not(:first-child)]:mt-4">
              <h2 className="mb-4 sm:mb-6 font-semibold text-[18px] sm:text-[32px]">{title}</h2>
              {content}
            </section>
          );
        })}
      </div>
    </TableOfContentLayout>
  );
};

const ProjectContentStatList = ({ tab }: ProjectContentTabProps) => {
  if (tab.type !== PROJECT_CONTENT_TAB_TYPE.STAT_LIST) {
    return null;
  }

  const isThreeColumns = tab?.layout === 3 && (tab.id === 'vesting' || tab.id === 'ido');

  return (
    <>
      <h2 className="font-semibold text-[2rem] leading-10 mb-6">{tab.label}</h2>

      <ul
        className={`grid sm:grid-cols-2 ${
          isThreeColumns ? 'lg:grid-cols-3' : 'lg:grid-cols-4'
        } gap-x-4 gap-y-6`}
      >
        {tab.content?.map(({ id, name, value, description }) => {
          return (
            <li key={id} className="flex flex-col gap-1">
              <span className="font-semibold text-2xl">{value}</span>

              <span className="font-medium gap-2 text-neutral-500 flex items-center">
                {name}
                {description && <BaseInfoTooltip content={description} iconClassName="size-5" />}
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
};

const tabTypeToComponent: Record<ProjectContentTabType, ComponentType<ProjectContentTabProps>> = {
  [PROJECT_CONTENT_TAB_TYPE.TABLE_OF_CONTENT]: ProjectContentTableOfContentComponent,
  [PROJECT_CONTENT_TAB_TYPE.CUSTOM]: ({ children }: ProjectContentTabProps) => <>{children}</>,
  [PROJECT_CONTENT_TAB_TYPE.STAT_LIST]: ProjectContentStatList,
};

export const ProjectContent = ({
  content,
  className,
}: {
  content: ProjectContentData;
  className?: string;
}) => {
  const [activeTabId, setActiveTabId] = useState(content.tabs[0].id);

  const activeTab = content.tabs.find((tab) => tab.id === activeTabId);

  const TabComponent = activeTab && tabTypeToComponent[activeTab.type];

  return (
    <>
      <div
        className={classNames(
          'flex overflow-x-auto hide-scrollbar border-b border-neutral-500',
          className,
        )}
      >
        <nav className="main-container flex flex-grow justify-start gap-5 items-start">
          {content.tabs.map(({ id, label }) => {
            const isActive = id === activeTabId;

            return (
              <button
                key={id}
                onClick={(event) => {
                  setActiveTabId(id);

                  const target = event.currentTarget;

                  target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }}
                className={classNames(
                  'font-semibold pb-[1.125rem] whitespace-nowrap transition-all border-b-4',
                  {
                    'border-secondary-700 text-secondary-700': isActive,
                    'border-transparent text-neutral-500': !isActive,
                  },
                )}
              >
                {label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab && TabComponent && (
        <div className="main-container leading-7 pt-4 font-semibold sm:pt-16">
          <TabComponent tab={activeTab} />
        </div>
      )}
    </>
  );
};

export default ProjectContent;
